import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Link2, Bot, Mic, Bell, Users, FileCheck, Gift, HeartPulse, Clock, ShieldCheck } from "lucide-react";

const fade = {
  hidden: { opacity: 0, y: 28 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.06, duration: 0.55, ease: [0.25, 0.46, 0.45, 0.94] } }),
};

const features = [
  { icon: Link2, title: "ABHA Integration", desc: "Seamless ABHA ID integration for universal patient identification across India." },
  { icon: Bot, title: "AI Agents", desc: "Dr. Mitra for summaries, Dr. Jeevan Rakshak for history, Dr. Saathi for patient support." },
  { icon: Mic, title: "Waiting Room Intake", desc: "Voice enabled, multilingual intake with customized AI generated mental and physical health questions. Zero stigma." },
  { icon: Bell, title: "Post-Visit Reminders", desc: "Automated follow-ups, medication alerts, and appointment scheduling." },
  { icon: Users, title: "Family Sharing", desc: "Consent-based family access to health records for better care coordination." },
  { icon: FileCheck, title: "One-Tap Insurance", desc: "Pre-populated claims with direct digital submission. One tap." },
  { icon: Gift, title: "Loyalty Incentives", desc: "Rewards for maintaining complete records with healthcare discounts." },
  { icon: HeartPulse, title: "Mental Health Support", desc: "Private, stigma-free screening woven into routine intake flows." },
];

const metrics = [
  { icon: Clock, stat: "2+ min", label: "saved per consult" },
  { icon: HeartPulse, stat: "40%", label: "more clinical context" },
  { icon: ShieldCheck, stat: "100%", label: "ABDM & DPDP ready" },
];

export const CapabilitiesSection = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section id="capabilities" className="py-20 lg:py-28" style={{ background: 'linear-gradient(180deg, #F5F5F0 0%, #F0F8FF 100%)' }} ref={ref}>
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <motion.div variants={fade} initial="hidden" animate={inView ? "visible" : "hidden"} className="text-center mb-12">
          <span className="text-spaced mb-3 block">U S E &nbsp; C A S E S</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold mb-4" style={{ color: '#1A1F36' }}>Built for Every Stakeholder</h2>
          <p className="text-base max-w-xl mx-auto" style={{ color: '#6B7280' }}>Comprehensive capabilities for patients, doctors, and hospitals.</p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-12">
          {features.map((f, i) => (
            <motion.div key={f.title}
              initial={{ opacity: 0, y: 30, scale: 0.95 }}
              animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
              transition={{ delay: i * 0.1 + 0.2, duration: 0.5 }}
            >
              <motion.div whileHover={{ y: -5, scale: 1.02 }} transition={{ duration: 0.2 }}>
                <Card className="h-full rounded-xl group flex flex-col border-0 shadow-lg overflow-hidden relative"
                  style={{ background: `linear-gradient(145deg, rgba(0,168,179,${0.06 + i * 0.02}), rgba(240,248,255,0.9))` }}>
                  <motion.div className="absolute inset-0 pointer-events-none"
                    style={{ background: 'linear-gradient(110deg, transparent 40%, rgba(0,168,179,0.04) 50%, transparent 60%)' }}
                    animate={{ x: ['-100%', '200%'] }}
                    transition={{ duration: 5, repeat: Infinity, delay: i * 0.3 }} />
                  <CardContent className="p-6 flex flex-col flex-1 relative z-10">
                    <motion.div whileHover={{ scale: 1.15, rotate: 8 }}
                      className="w-10 h-10 rounded-xl flex items-center justify-center mb-3 transition-colors"
                      style={{ background: 'rgba(0,168,179,0.12)' }}>
                      <f.icon className="w-5 h-5" style={{ color: '#00A8B3' }} />
                    </motion.div>
                    <h4 className="text-sm font-heading font-semibold mb-1.5" style={{ color: '#1A1F36' }}>{f.title}</h4>
                    <p className="text-xs leading-relaxed" style={{ color: '#6B7280' }}>{f.desc}</p>
                  </CardContent>
                </Card>
              </motion.div>
            </motion.div>
          ))}
        </div>

        {/* Metrics row */}
        <motion.div variants={fade} initial="hidden" animate={inView ? "visible" : "hidden"} custom={10}>
          <div className="gradient-border rounded-2xl">
            <Card className="glass-card border-0 rounded-2xl">
              <CardContent className="p-8">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                  {metrics.map(m => (
                    <div key={m.stat} className="text-center">
                      <m.icon className="w-7 h-7 text-primary mx-auto mb-2" />
                      <p className="text-2xl font-heading font-bold text-foreground">{m.stat}</p>
                      <p className="text-xs text-muted-foreground">{m.label}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
